/**
 * Data Share — read an export file IN THE BROWSER. Nothing is uploaded.
 *
 * Accepts:
 *   - the ChatGPT data export (.zip with conversations.json; every chat, including chats in projects);
 *   - the Claude data export (.zip with conversations.json and projects.json);
 *   - the DeepSeek data export (.zip with conversations.json, fragments per message; user.json is skipped);
 *   - the Claude memory export (.zip of markdown files with name/description front matter, e.g.
 *     claude-memory-2026-11-04.zip: profile.md, areas/*.md, topics/*.md);
 *   - any of those files on its own (.json or .md).
 *
 * The zip is read with a small reader over Blob.slice + DecompressionStream, so only the entries we
 * need are inflated and a multi-GB ChatGPT export (mostly images) never has to fit in memory. ZIP64
 * is handled because large ChatGPT exports use it. Encrypted entries are refused.
 */

import {
  chatgptConversation,
  claudeConversation,
  deepseekExportConversation,
  documentsConversation,
  conversationFromSavedPage,
  isDeepseekExportRecord,
  memoryDoc,
} from "./normalise";
import { PROVIDER_LABEL } from "./providers";
import type { ShareConversation, ShareMessage, ShareProvider } from "./types";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

const MAX_ENTRY_BYTES = 1_500 * 1024 * 1024; // one JSON file; beyond this the browser tab will struggle
const MAX_MD_FILES = 2_000;

export type ExportResult = {
  conversations: ShareConversation[];
  /** Plain notes about what was read or skipped, shown under the results. */
  notes: string[];
};

type ZipEntry = {
  name: string;
  method: number;
  flags: number;
  compressedSize: number;
  size: number;
  localOffset: number;
};

/* ───────────────────────────── zip reader ───────────────────────────── */

async function bytes(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function u64(v: DataView, o: number): number {
  return v.getUint32(o, true) + v.getUint32(o + 4, true) * 2 ** 32;
}

async function listZip(file: Blob): Promise<ZipEntry[]> {
  const tailLen = Math.min(file.size, 65_557 + 20);
  const tail = await bytes(file, file.size - tailLen, file.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This doesn't look like a zip file.");

  let count = tail.getUint16(eocd + 10, true);
  let cdSize = tail.getUint32(eocd + 12, true);
  let cdOffset = tail.getUint32(eocd + 16, true);

  if (count === 0xffff || cdSize === 0xffffffff || cdOffset === 0xffffffff) {
    // ZIP64: the locator sits 20 bytes before the EOCD and points at the ZIP64 EOCD record.
    const loc = eocd - 20;
    if (loc < 0 || tail.getUint32(loc, true) !== 0x07064b50) throw new Error("This zip is damaged.");
    const recOffset = u64(tail, loc + 8);
    const rec = await bytes(file, recOffset, recOffset + 56);
    if (rec.getUint32(0, true) !== 0x06064b50) throw new Error("This zip is damaged.");
    count = u64(rec, 32);
    cdSize = u64(rec, 40);
    cdOffset = u64(rec, 48);
  }

  const cd = await bytes(file, cdOffset, cdOffset + cdSize);
  const dec = new TextDecoder("utf-8");
  const out: ZipEntry[] = [];
  let p = 0;
  for (let n = 0; n < count && p + 46 <= cd.byteLength; n++) {
    if (cd.getUint32(p, true) !== 0x02014b50) break;
    const flags = cd.getUint16(p + 8, true);
    const method = cd.getUint16(p + 10, true);
    let compressedSize = cd.getUint32(p + 20, true);
    let size = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    let localOffset = cd.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));

    // ZIP64 extra field (0x0001) holds whichever of the three values overflowed, in this order.
    let e = p + 46 + nameLen;
    const eEnd = e + extraLen;
    while (e + 4 <= eEnd) {
      const id = cd.getUint16(e, true);
      const len = cd.getUint16(e + 2, true);
      if (id === 0x0001) {
        let q = e + 4;
        if (size === 0xffffffff) { size = u64(cd, q); q += 8; }
        if (compressedSize === 0xffffffff) { compressedSize = u64(cd, q); q += 8; }
        if (localOffset === 0xffffffff) { localOffset = u64(cd, q); q += 8; }
      }
      e += 4 + len;
    }

    out.push({ name, method, flags, compressedSize, size, localOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function readEntry(file: Blob, e: ZipEntry): Promise<string> {
  if (e.flags & 0x1) throw new Error(`${e.name} is password-protected.`);
  const h = await bytes(file, e.localOffset, e.localOffset + 30);
  if (h.getUint32(0, true) !== 0x04034b50) throw new Error(`Couldn't read ${e.name}.`);
  const start = e.localOffset + 30 + h.getUint16(26, true) + h.getUint16(28, true);
  const raw = file.slice(start, start + e.compressedSize);
  if (e.method === 0) return raw.text();
  if (e.method !== 8) throw new Error(`${e.name} uses a compression we can't read.`);
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser can't open zip files. Try a current Chrome, Edge, Firefox or Safari.");
  }
  const stream = raw.stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(stream).text();
}

/* ───────────────────────────── shape detection ───────────────────────────── */

function fromConversationsJson(data: Any, notes: string[]): ShareConversation[] {
  const list: Any[] = Array.isArray(data) ? data : Array.isArray(data?.conversations) ? data.conversations : [];
  const out: ShareConversation[] = [];
  let skipped = 0;
  for (const c of list) {
    // DeepSeek's export also uses a `mapping` tree, but its messages are typed fragments.
    if (isDeepseekExportRecord(c)) out.push(deepseekExportConversation(c));
    else if (c && typeof c === "object" && "mapping" in c) out.push(chatgptConversation(c, "export"));
    else if (c && typeof c === "object" && "chat_messages" in c) out.push(claudeConversation(c, "export"));
    else skipped++;
  }
  if (skipped) notes.push(`${skipped} item${skipped === 1 ? "" : "s"} in the file didn't look like a conversation and were skipped.`);
  return out.filter((c) => c.messages.length > 0);
}

function fromClaudeProjects(data: Any): ShareConversation[] {
  const list: Any[] = Array.isArray(data) ? data : [];
  const out: ShareConversation[] = [];
  for (const p of list) {
    const docs: ShareMessage[] = [];
    const desc = [p?.description, p?.prompt_template].filter((x) => typeof x === "string" && x.trim()).join("\n\n");
    if (desc) docs.push({ role: "user", kind: "document", name: "Project instructions", text: desc });
    for (const d of Array.isArray(p?.docs) ? p.docs : []) {
      if (typeof d?.content === "string" && d.content.trim()) {
        docs.push({ role: "user", kind: "document", name: String(d.filename || "document"), text: d.content });
      }
    }
    if (docs.length) {
      out.push(documentsConversation("claude", "project", String(p?.uuid || p?.name || "project"), `Project: ${p?.name || "Untitled"}`, docs));
    }
  }
  return out;
}

/** Claude's memories.json: shape not fixed, so take the readable text out of it. */
function fromMemoriesJson(data: Any): ShareConversation[] {
  const docs: ShareMessage[] = [];
  const walk = (v: Any, key: string) => {
    if (typeof v === "string") {
      if (v.trim().length > 20) docs.push({ role: "user", kind: "memory", name: key || "memory", text: v.trim() });
    } else if (Array.isArray(v)) v.forEach((x) => walk(x, key));
    else if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) if (!/uuid|_id$|^id$|_at$/i.test(k)) walk(x, k);
  };
  walk(data, "");
  return docs.length ? [documentsConversation("claude", "memory", "claude-memories", "Claude memory", docs)] : [];
}

function providerOf(convs: ShareConversation[]): ShareProvider | null {
  return convs[0]?.provider ?? null;
}

async function parseJsonText(name: string, text: string, notes: string[]): Promise<ShareConversation[]> {
  let data: Any;
  try {
    data = JSON.parse(text);
  } catch {
    notes.push(`${name} isn't valid JSON and was skipped.`);
    return [];
  }
  const base = name.split("/").pop()?.toLowerCase() || "";
  if (base === "projects.json") return fromClaudeProjects(data);
  if (base === "memories.json") return fromMemoriesJson(data);
  if (base === "users.json" || base === "user.json" || base === "message_feedback.json" || base === "shared_conversations.json") {
    return []; // account details, not conversations
  }
  // A single conversation object (e.g. a hand-saved share snapshot) or a list.
  if (data && !Array.isArray(data) && ("chat_messages" in data || "mapping" in data)) {
    return fromConversationsJson([data], notes);
  }
  return fromConversationsJson(data, notes);
}

/* ───────────────────────────── entry point ───────────────────────────── */

export async function readExportFile(file: File): Promise<ExportResult> {
  const notes: string[] = [];
  const lower = file.name.toLowerCase();

  if (lower.endsWith(".json")) {
    if (file.size > MAX_ENTRY_BYTES) throw new Error("That file is too large to read in the browser.");
    return { conversations: await parseJsonText(file.name, await file.text(), notes), notes };
  }
  if (lower.endsWith(".md") || lower.endsWith(".markdown")) {
    const doc = memoryDoc(file.name, await file.text());
    return { conversations: [documentsConversation("claude", "memory", file.name, "Claude memory", [doc])], notes };
  }
  if (lower.endsWith(".html") || lower.endsWith(".htm") || lower.endsWith(".mhtml")) {
    if (file.size > 60 * 1024 * 1024) throw new Error("That page is too large to read in the browser.");
    const conv = conversationFromSavedPage(await file.text());
    if (!conv) {
      throw new Error(
        "We couldn't find a conversation in that page. Save a ChatGPT or Mistral share page with Ctrl+S (Webpage, HTML only) after it has fully loaded.",
      );
    }
    notes.push(`Read from a ${PROVIDER_LABEL[conv.provider]} page you saved, entirely in your browser.`);
    return { conversations: [conv], notes };
  }
  if (!lower.endsWith(".zip")) {
    throw new Error("Upload the .zip from ChatGPT, Claude or DeepSeek, a conversations.json, a memory .md file, or a saved ChatGPT or Mistral share page (.html).");
  }

  const entries = (await listZip(file)).filter((e) => !e.name.endsWith("/") && !e.name.startsWith("__MACOSX/"));
  const convs: ShareConversation[] = [];

  const jsons = entries.filter((e) => /(?:^|\/)(?:conversations[^/]*|projects|memories)\.json$/i.test(e.name));
  for (const e of jsons) {
    if (e.size > MAX_ENTRY_BYTES) {
      notes.push(`${e.name} is too large to read in the browser and was skipped.`);
      continue;
    }
    convs.push(...(await parseJsonText(e.name, await readEntry(file, e), notes)));
  }

  const mds = entries.filter((e) => /\.(?:md|markdown)$/i.test(e.name) && !/(?:^|\/)readme\.md$/i.test(e.name));
  if (mds.length && !jsons.length) {
    const docs: ShareMessage[] = [];
    for (const e of mds.slice(0, MAX_MD_FILES)) docs.push(memoryDoc(e.name, await readEntry(file, e)));
    if (mds.length > MAX_MD_FILES) notes.push(`Only the first ${MAX_MD_FILES} notes were read.`);
    const title = file.name.replace(/\.zip$/i, "");
    convs.push(documentsConversation("claude", "memory", title, `Memory: ${title}`, docs.filter((d) => d.text)));
  }

  if (!convs.length) {
    throw new Error(
      "We couldn't find any conversations in that zip. Use the export ChatGPT, Claude or DeepSeek sends you (it contains conversations.json), or a Claude memory export.",
    );
  }

  const skippedOther = entries.length - jsons.length - (jsons.length ? 0 : mds.length);
  if (skippedOther > 0) {
    notes.push(`${skippedOther.toLocaleString()} other file${skippedOther === 1 ? "" : "s"} in the zip (images, audio, account details) were left alone.`);
  }
  const p = providerOf(convs);
  if (p) notes.unshift(`Read as a ${PROVIDER_LABEL[p]} export, entirely in your browser.`);
  return { conversations: convs, notes };
}
